<template>
  <v-container class="py-12">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">DRE gerencial</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
              { text: 'Home', to: '/home' },
              { text: 'Financeiro', to: '/financeiro', exact: true },
              { text: 'DRE', disabled: true }
            ]" />
          </div>
          <div class="button-group">
            <v-btn outlined color="primary" elevation="0" :loading="exporting" :disabled="!data" @click="exportDre">
              <v-icon left small>mdi-file-excel-outline</v-icon>Exportar
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <period-filter :loading="loading" @change="onPeriod" />

    <div v-if="data" class="stat-grid">
      <stat-card label="Receita líquida" :value="money(data.totals.net_revenue)" color="#1450b5" :hint="`${data.totals.trips} viagens finalizadas`" />
      <stat-card label="Margem de contribuição" :value="money(data.totals.contribution)" hint="receita menos custos operacionais" />
      <stat-card label="Resultado" :value="money(data.totals.result)" :color="data.totals.result < 0 ? '#d32f2f' : '#2e7d32'"
        :hint="data.totals.margin_percent !== null ? `margem de ${percent(data.totals.margin_percent)}` : '-'" />
    </div>

    <v-card v-if="data">
      <div class="dre-wrapper">
        <table class="dre">
          <thead>
            <tr>
              <th class="dre__label"></th>
              <th v-for="month in data.months" :key="month">{{ monthLabel(month) }}</th>
              <th class="dre__total">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in data.lines" :key="row.key" :class="rowClass(row)">
              <td class="dre__label" :title="row.label">{{ row.label }}</td>
              <td v-for="month in data.months" :key="month" :class="valueClass(row, row.values[month])">
                {{ format(row, row.values[month]) }}
              </td>
              <td class="dre__total" :class="valueClass(row, row.total)">{{ format(row, row.total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>

    <v-card v-if="data" class="section-card">
      <v-card-title><div>Como cada linha é calculada</div></v-card-title>
      <v-card-text class="body-2 dre-notes">
        <p><strong>Receita bruta:</strong> valor dos passageiros das viagens finalizadas, pela data da viagem (o mesmo do relatório do cliente).
          O pedágio é separado por ser repasse ao cliente.</p>
        <p><strong>Motoristas parceiros:</strong> valor por localidade das viagens finalizadas (mesma regra do fechamento).
          <strong>Funcionários:</strong> salário vigente + encargos cadastrados no motorista, proporcional aos dias do período.</p>
        <p><strong>Combustível e manutenção:</strong> abastecimentos e manutenções (sem rascunho/cancelada) pela data.
          <strong>Custos fixos de veículos:</strong> aluguel, financiamento, IPVA, seguro rateados por mês.</p>
        <p class="mb-0"><strong>Despesas:</strong> lançamentos de despesas pelo mês de competência (sem canceladas), agrupados pelo grupo da categoria.
          Despesas com veículo entram em custos operacionais.</p>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import PeriodFilter from '@/layouts/components/financial/PeriodFilter.vue'

export default {
  name: 'FinancialDre',

  components: { PeriodFilter },

  data: () => ({
    loading: false,
    exporting: false,
    range: null,
    data: null
  }),

  methods: {
    async onPeriod(range) {
      this.range = range
      this.loading = true
      try {
        const response = await axios.get('/api/v2/financial/reports/dre', { params: range })
        this.data = response.data.content
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
      } finally {
        this.loading = false
      }
    },

    async exportDre() {
      this.exporting = true
      try {
        const response = await axios.get('/api/v2/financial/reports/dre/export', { params: this.range, responseType: 'blob' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(new Blob([response.data]))
        link.download = `dre-${this.range.start}-a-${this.range.end}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      } catch (error) {
        this.$dialog.notify.error('Não foi possível exportar', { position: 'top-right', timeout: 4000 })
      } finally {
        this.exporting = false
      }
    },

    rowClass(row) {
      return {
        'dre__row--total': !row.level,
        'dre__row--detail': row.level === 1,
        'dre__row--result': row.key === 'result'
      }
    },

    valueClass(row, value) {
      if (row.percent || value === null) return ''
      if (row.key === 'result' || row.key === 'contribution') return value < 0 ? 'red--text' : 'green--text text--darken-2'
      return value < 0 && row.level ? 'dre__negative' : ''
    },

    format(row, value) {
      if (row.percent) return value === null ? '-' : this.percent(value)
      if (!value) return '-'
      return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    monthLabel(month) {
      const label = moment(`${month}-01`).locale('pt-br').format('MMM/YY')
      return label.charAt(0).toUpperCase() + label.slice(1)
    },

    money(value) {
      return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },

    percent(value) {
      return `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
    }
  }
}
</script>

<style lang="scss" scoped>
.dre-wrapper {
  overflow-x: auto;
}

.dre {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 9px 12px;
    text-align: right;
    white-space: nowrap;
    border-bottom: 1px solid #eef1f6;
  }

  th {
    background: #f7f8fb;
    color: #5b6476;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .dre__label {
    position: sticky;
    left: 0;
    min-width: 300px;
    max-width: 360px;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
    background: #fff;
    z-index: 1;
  }

  th.dre__label {
    background: #f7f8fb;
  }

  .dre__total {
    font-weight: 700;
    background: #fafbfd;
  }
}

.dre__row--total td {
  font-weight: 700;
  color: #10213f;
}

.dre__row--detail td {
  color: #52627d;
}

.dre__row--detail .dre__label {
  padding-left: 32px;
}

.dre__row--result td {
  background: #f1f6ff;
  border-top: 2px solid #bdd4ff;
  font-size: 14px;
}

.dre__row--result .dre__label {
  background: #f1f6ff;
}

.dre__negative {
  color: #8a5a5a;
}

.dre-notes p {
  margin-bottom: 8px;
  color: #52627d;
}
</style>
